import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSheinWorkspaceAuth } from '@/shein-platform/context/SheinWorkspaceAuthContext';
import { IPE_SHEIN_GATE_PATH } from '@/shein-platform/constants/sheinRoutes';
import {
  hasAuthorizedSheinShop,
} from '@/shein-platform/utils/sheinShopAuthGate';

/**
 * 二次验证通过后，仍须有已授权 SHEIN 店铺才可进入工作区。
 */
export default function SheinWorkspaceGuard({ children }) {
  const { t } = useTranslation('workbench');
  const { unlocked, checking, lock } = useSheinWorkspaceAuth();
  const location = useLocation();
  const [shopAuthChecking, setShopAuthChecking] = useState(false);
  const [shopAuthDenied, setShopAuthDenied] = useState(false);

  useEffect(() => {
    if (checking || !unlocked) {
      setShopAuthChecking(false);
      setShopAuthDenied(false);
      return undefined;
    }

    let cancelled = false;
    setShopAuthChecking(true);
    setShopAuthDenied(false);

    hasAuthorizedSheinShop()
      .then(async (ok) => {
        if (cancelled) return;
        if (ok) {
          setShopAuthDenied(false);
          return;
        }
        setShopAuthDenied(true);
        try {
          await lock();
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShopAuthDenied(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setShopAuthChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [checking, unlocked, lock]);

  if (checking || shopAuthChecking) {
    return (
      <div className="shein-platform shein-platform--loading">
        <p>{t('platformBar.checking', { platform: 'SHEIN' })}</p>
      </div>
    );
  }

  if (!unlocked || shopAuthDenied) {
    return (
      <Navigate
        to={IPE_SHEIN_GATE_PATH}
        replace
        state={{
          redirect: location.pathname + location.search,
          shopAuthError: shopAuthDenied ? t('shop.enterAuthExpired') : undefined,
        }}
      />
    );
  }

  return children || <Outlet />;
}
